import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { FeedbackReport } from "@/lib/tcf";
import { PenLine, Clock, Type, Loader2, ArrowLeft, RotateCcw, ShieldCheck, History } from "lucide-react";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function WritingLab() {
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const timerRef = useRef(null);

  const loadHistory = () => api.get("/tcf/writing-history").then((r) => setHistory(r.data.attempts || [])).catch(() => {});

  useEffect(() => { api.get("/tcf/writing-tasks").then((r) => setTasks(r.data)).catch((e) => console.error("tasks", e)); }, []);
  useEffect(() => { loadHistory(); }, []);
  useEffect(() => {
    if (running) timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  const open = (t) => { setActive(t); setText(""); setSeconds(0); setRunning(true); setResult(null); };
  const viewAttempt = (a) => {
    setActive(tasks.find((t) => t.id === a.task_id) || { id: a.task_id, title: a.task_title });
    setText(a.text || ""); setResult(a.result); setRunning(false); window.scrollTo({ top: 0 });
  };
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;

  const submit = async () => {
    if (words < 10) { toast.error("Écrivez au moins quelques phrases."); return; }
    setRunning(false); setLoading(true);
    try {
      const { data } = await api.post("/tcf/evaluate-writing", { task_id: active.id, text });
      setResult(data);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Évaluation impossible.");
    } finally { setLoading(false); }
  };

  if (result) {
    return (
      <div data-testid="writing-lab-result">
        <button onClick={() => { setActive(null); setResult(null); }} className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="h-4 w-4" /> Back to tasks</button>
        <div className="flex items-center justify-between mb-5">
          <h1 className="font-display text-3xl font-semibold">Your feedback</h1>
          <button onClick={() => open(active)} data-testid="wl-retry" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><RotateCcw className="h-4 w-4" /> Try again</button>
        </div>
        <FeedbackReport result={result} userText={text} />
      </div>
    );
  }

  if (active) {
    const enough = words >= active.min_words;
    return (
      <div data-testid="writing-lab-editor">
        <button onClick={() => setActive(null)} className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="h-4 w-4" /> Back to tasks</button>
        <div className="rounded-2xl bg-card border border-border p-6">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <span className="px-2.5 py-1 rounded-full bg-accent text-accent-foreground">{active.target}</span>
            <span className="px-2.5 py-1 rounded-full bg-secondary">{active.min_words}-{active.max_words} mots</span>
            <span className="px-2.5 py-1 rounded-full bg-secondary">{active.minutes} min</span>
          </div>
          <h1 className="font-display text-2xl font-semibold mt-4">{active.title}</h1>
          <p className="text-muted-foreground mt-2 leading-relaxed">{active.prompt}</p>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-2 text-sm font-semibold"><Clock className="h-4 w-4 text-primary" /> {fmt(seconds)}</span>
            <span className={`inline-flex items-center gap-2 text-sm font-semibold ${enough ? "text-primary" : "text-muted-foreground"}`}><Type className="h-4 w-4" /> {words} / {active.min_words} mots</span>
          </div>
          <button onClick={() => setRunning((r) => !r)} className="text-sm font-semibold text-primary hover:underline">{running ? "Pause" : "Reprendre"}</button>
        </div>

        <textarea data-testid="wl-textarea" value={text} onChange={(e) => setText(e.target.value)} placeholder="Rédigez votre réponse ici…"
          className="mt-3 w-full min-h-[300px] p-5 rounded-2xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-ring leading-relaxed resize-y" />

        <button onClick={submit} disabled={loading} data-testid="wl-submit"
          className="mt-4 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Correction en cours…</> : "Obtenir mon feedback"}
        </button>
        {loading && <p className="text-sm text-muted-foreground mt-2">L'IA estime votre niveau et prépare vos réécritures CLB5 et CLB7…</p>}
      </div>
    );
  }

  const byTask = history.reduce((a, x) => { (a[x.task_id] = a[x.task_id] || []).push(x); return a; }, {});
  return (
    <div data-testid="writing-lab-page">
      <h1 className="font-display text-4xl font-semibold">Writing Lab</h1>
      <p className="text-muted-foreground mt-2">Write a timed TCF task and get a tentative level estimate with clear tips and your answer rewritten at CLB5 and CLB7.</p>
      <div className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Level estimate guided by the official TCF Canada criteria.</div>

      <div className="grid sm:grid-cols-3 gap-5 mt-8">
        {tasks.map((t, i) => {
          const n = (byTask[t.id] || []).length;
          return (
            <motion.button key={t.id} onClick={() => open(t)} data-testid={`wl-task-${t.id}`}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
              className="text-left rounded-2xl bg-card border border-border p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
              <div className="flex items-center justify-between">
                <div className="h-11 w-11 rounded-xl emerald-surface grid place-items-center"><PenLine className="h-5 w-5 text-white" /></div>
                {n > 0 && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-accent text-accent-foreground" data-testid={`wl-task-count-${t.id}`}>{n} saved</span>}
              </div>
              <h3 className="mt-4 font-semibold">{t.title}</h3>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{t.prompt}</p>
              <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-primary">
                <span className="px-2 py-0.5 rounded-full bg-accent">{t.target}</span>
                <span>{t.minutes} min · {t.min_words}-{t.max_words} mots</span>
              </div>
            </motion.button>
          );
        })}
      </div>

      {history.length > 0 && (
        <div className="mt-12" data-testid="wl-history">
          <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><History className="h-5 w-5 text-primary" /> Your past writings</h2>
          <p className="text-sm text-muted-foreground mt-1">Every answer you've submitted, saved by task. Open one to re-read your text and its feedback.</p>
          <div className="mt-5 space-y-7">
            {Object.entries(byTask).map(([tid, items]) => (
              <div key={tid} data-testid={`wl-history-task-${tid}`}>
                <h3 className="font-semibold text-sm text-foreground mb-2">{items[0].task_title} <span className="text-muted-foreground font-normal">· {items.length} answer{items.length > 1 ? "s" : ""}</span></h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((a) => (
                    <button key={a.id} onClick={() => viewAttempt(a)} data-testid={`wl-history-${a.id}`}
                      className="text-left rounded-xl bg-card border border-border p-4 hover:shadow-md hover:border-primary/40 transition-all">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-accent text-accent-foreground font-semibold">CLB {a.result?.clb}{a.result?.cefr ? ` · ${a.result.cefr}` : ""}</span>
                        <span className="text-[11px] text-muted-foreground">{fmtDate(a.created_at)}</span>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-2 whitespace-pre-wrap">{a.text}</p>
                      <span className="mt-2 inline-block text-xs font-semibold text-primary">View feedback →</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
